import React, { useEffect, useState } from 'react';
import {
    Card,
    Typography,
    Space,
    Row,
    Col,
    Statistic,
    Progress,
    Descriptions,
    Button,
    Spin,
    Empty,
    Tag,
    Divider,
    Table,
    Badge,
    Tooltip,
    Modal,
    Form,
    Switch,
    InputNumber,
    Input,
    Select,
    TimePicker,
    DatePicker,
    message,
    ConfigProvider,
} from 'antd';
import {
    BookOutlined,
    EditOutlined,
    SaveOutlined,
    CloseOutlined,
    ClockCircleOutlined,
    UserOutlined,
    TeamOutlined,
    FileTextOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    ReloadOutlined,
    CalendarOutlined,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import 'dayjs/locale/lt';
import locale from 'antd/locale/lt_LT';

import { AppDispatch, CombinedStates } from '../../core/types';
import { getLibrary, createOrUpdateLibrary } from '../../core/center-accreditation/centerAccreditationApi';
import { CenterAccreditationLibrary as ScLibraryType } from '../../core/center-accreditation/types';
import { convertToBoolean, generateTimeOptions, getPermissionLabel, getDayLabel, getMaterialTypeOptions } from '../../core/utils/helpers';

import './ScLibrary.scss';

// Set Lithuanian locale for dayjs
dayjs.locale('lt');

const { Title, Text } = Typography;
const { Option } = Select;

const timeOptions = generateTimeOptions();
const materialTypeOptions = getMaterialTypeOptions();

// Predefined permission keys to ensure they always render
const lendingPermissionKeys = ['institution_staff', 'lecturers', 'event_participants', 'region_teachers', 'all_teachers', 'students', 'anyone'];
const copyingPermissionKeys = ['institution_staff', 'lecturers', 'event_participants', 'region_teachers', 'all_teachers', 'students', 'anyone'];

const ScLibrary: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { library, isLibraryFetching } = useSelector((state: CombinedStates) => state.centerAccreditation);
    const { singleSchool } = useSelector((state: CombinedStates) => state.school);
    const { userData } = useSelector((state: CombinedStates) => state.user);

    const [form] = Form.useForm();
    const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(dayjs());

    useEffect(() => {
        dispatch(getLibrary({}));
    }, []);

    // Set form values when library data is loaded
    useEffect(() => {
        if (library) {
            form.setFieldsValue({
                ...library,
                // Convert string booleans to actual booleans
                has_system_catalog: convertToBoolean(library.has_system_catalog),
                has_alphabetical_catalog: convertToBoolean(library.has_alphabetical_catalog),
                has_books_list: convertToBoolean(library.has_books_list),
                stores_event_materials: convertToBoolean(library.stores_event_materials),
                has_e_media: convertToBoolean(library.has_e_media),
                satisfaction_percent: library.satisfaction_percent || 0,
                // Convert notification string booleans
                notification: {
                    website_lists: convertToBoolean(library.notification?.website_lists),
                    email_post: convertToBoolean(library.notification?.email_post),
                    notice_board: convertToBoolean(library.notification?.notice_board),
                    other: library.notification?.other || '',
                },
                // Convert e_media_catalogs string booleans
                e_media_catalogs: {
                    system: convertToBoolean(library.e_media_catalogs?.system),
                    alphabetical: convertToBoolean(library.e_media_catalogs?.alphabetical),
                    cd_list: convertToBoolean(library.e_media_catalogs?.cd_list),
                },
                // Lending permissions as strings
                lending_permissions: {
                    institution_staff: library.lending_permissions?.institution_staff || '',
                    lecturers: library.lending_permissions?.lecturers || '',
                    event_participants: library.lending_permissions?.event_participants || '',
                    region_teachers: library.lending_permissions?.region_teachers || '',
                    all_teachers: library.lending_permissions?.all_teachers || '',
                    students: library.lending_permissions?.students || '',
                    anyone: library.lending_permissions?.anyone || '',
                },
                // Convert copying_permissions string booleans
                copying_permissions: {
                    institution_staff: convertToBoolean(library.copying_permissions?.institution_staff),
                    lecturers: convertToBoolean(library.copying_permissions?.lecturers),
                    event_participants: convertToBoolean(library.copying_permissions?.event_participants),
                    region_teachers: convertToBoolean(library.copying_permissions?.region_teachers),
                    all_teachers: convertToBoolean(library.copying_permissions?.all_teachers),
                    students: convertToBoolean(library.copying_permissions?.students),
                    anyone: convertToBoolean(library.copying_permissions?.anyone),
                },
                // Convert surveys string booleans
                surveys: {
                    conducted: convertToBoolean(library.surveys?.conducted),
                    methods: {
                        requests_analysis: convertToBoolean(library.surveys?.methods?.requests_analysis),
                        institution_surveys: convertToBoolean(library.surveys?.methods?.institution_surveys),
                        other: library.surveys?.methods?.other || '',
                    },
                },
                working_hours: {
                    library: {
                        mon: { from: library.working_hours.library.mon?.from || null, to: library.working_hours.library.mon?.to || null },
                        tue: { from: library.working_hours.library.tue?.from || null, to: library.working_hours.library.tue?.to || null },
                        wed: { from: library.working_hours.library.wed?.from || null, to: library.working_hours.library.wed?.to || null },
                        thu: { from: library.working_hours.library.thu?.from || null, to: library.working_hours.library.thu?.to || null },
                        fri: { from: library.working_hours.library.fri?.from || null, to: library.working_hours.library.fri?.to || null },
                        sat: { from: library.working_hours.library.sat?.from || null, to: library.working_hours.library.sat?.to || null },
                        sun: { from: library.working_hours.library.sun?.from || null, to: library.working_hours.library.sun?.to || null },
                    },
                    reading_room: {
                        mon: { from: library.working_hours.reading_room.mon?.from || null, to: library.working_hours.reading_room.mon?.to || null },
                        tue: { from: library.working_hours.reading_room.tue?.from || null, to: library.working_hours.reading_room.tue?.to || null },
                        wed: { from: library.working_hours.reading_room.wed?.from || null, to: library.working_hours.reading_room.wed?.to || null },
                        thu: { from: library.working_hours.reading_room.thu?.from || null, to: library.working_hours.reading_room.thu?.to || null },
                        fri: { from: library.working_hours.reading_room.fri?.from || null, to: library.working_hours.reading_room.fri?.to || null },
                        sat: { from: library.working_hours.reading_room.sat?.from || null, to: library.working_hours.reading_room.sat?.to || null },
                        sun: { from: library.working_hours.reading_room.sun?.from || null, to: library.working_hours.reading_room.sun?.to || null },
                    },
                },
            });
        }
    }, [library, form]);

    const handleSave = async (values: any) => {
        // No need to convert time ranges since they are already strings
        const requestData = {
            ...values,
            school_id: singleSchool?.id,
            id: library?.id,
            date: selectedDate ? selectedDate.format('YYYY-MM-DD') : null,
        };

        await dispatch(createOrUpdateLibrary(requestData));
    };

    const handleRefresh = (date?: dayjs.Dayjs) => {
        const dateToUse = date || selectedDate;
        dispatch(getLibrary({ date: dateToUse ? dateToUse.format('YYYY-MM-DD') : null }));
    };

    const handleRefreshClick = () => {
        handleRefresh();
    };

    return (
        <ConfigProvider locale={locale}>
            <div className="school-library">
                <Card title="Bibliotekos duomenys" className="library-form-card" loading={isLibraryFetching || false}>
                    <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                            {library?.period_description ? (
                                <Text strong style={{ fontSize: '16px' }}>
                                    <CalendarOutlined style={{ marginRight: 8 }} />
                                    {library.period_description}
                                </Text>
                            ) : (
                                <Text strong style={{ fontSize: '16px' }}>
                                    Pasirinktam laikotarpiui duomenys nepateikti, norėdami pateikti duomenis užpildykite ir išsaugokite formą.
                                </Text>
                            )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <DatePicker
                                placeholder="Pasirinkite datą"
                                value={selectedDate}
                                onChange={(date) => {
                                    const newDate = date || dayjs();
                                    setSelectedDate(newDate);
                                    handleRefresh(newDate);
                                }}
                                format="YYYY-MM-DD"
                                style={{ width: 180 }}
                                allowClear={false}
                                disabledDate={(current) => current && current > dayjs().endOf('day')} />
                            <Tooltip title="Atnaujinti bibliotekos duomenis">
                                <Button
                                    icon={<ReloadOutlined />}
                                    onClick={handleRefreshClick}
                                    loading={!!isLibraryFetching}>
                                    Atnaujinti
                                </Button>
                            </Tooltip>
                        </div>
                    </div>
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSave}
                        initialValues={{
                            has_system_catalog: false,
                            has_alphabetical_catalog: false,
                            has_books_list: false,
                            books_titles_count: 0,
                            books_copies_count: 0,
                            events_count: 0,
                            collaborations: {
                                public: 0,
                                school: 0,
                                university: 0,
                            },
                            notification: {
                                website_lists: false,
                                email_post: false,
                                notice_board: false,
                                other: '',
                            },
                            stores_event_materials: false,
                            staff_full_time: 0,
                            staff_partial: 0,
                            has_e_media: false,
                            e_media_catalogs: {
                                system: false,
                                alphabetical: false,
                                cd_list: false,
                            },
                            cd_titles_count: 0,
                            cd_copies_count: 0,
                            lending_permissions: {
                                institution_staff: '',
                                lecturers: '',
                                event_participants: '',
                                region_teachers: '',
                                all_teachers: '',
                                students: '',
                                anyone: '',
                            },
                            copying_permissions: {
                                institution_staff: false,
                                lecturers: false,
                                event_participants: false,
                                region_teachers: false,
                                all_teachers: false,
                                students: false,
                                anyone: false,
                            },
                            reading_room_seats: {
                                print: 0,
                                internet: 0,
                            },
                            working_hours: {
                                library: {
                                    mon: { from: null, to: null },
                                    tue: { from: null, to: null },
                                    wed: { from: null, to: null },
                                    thu: { from: null, to: null },
                                    fri: { from: null, to: null },
                                    sat: { from: null, to: null },
                                    sun: { from: null, to: null },
                                },
                                reading_room: {
                                    mon: { from: null, to: null },
                                    tue: { from: null, to: null },
                                    wed: { from: null, to: null },
                                    thu: { from: null, to: null },
                                    fri: { from: null, to: null },
                                    sat: { from: null, to: null },
                                    sun: { from: null, to: null },
                                },
                            },
                            readers_stats: {
                                total: 0,
                                institution_staff: 0,
                                lecturers: 0,
                                event_participants: 0,
                                region_teachers: 0,
                                students: 0,
                                other: {
                                    label: '',
                                    count: 0,
                                },
                            },
                            surveys: {
                                conducted: false,
                                methods: {
                                    requests_analysis: false,
                                    institution_surveys: false,
                                    other: '',
                                },
                            },
                            satisfaction_percent: 0,
                        }}>

                        {/* 1.1 Ar bibliotekoje yra */}
                        <Card title="1.1 Ar bibliotekoje yra" style={{ marginBottom: 16 }}>
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Form.Item name="has_system_catalog" label="1.1.1 Sisteminis katalogas" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name="has_alphabetical_catalog" label="1.1.2 Abėcėlinis katalogas" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name="has_books_list" label="1.1.3 Knygų sąrašas" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        {/* 1.2 Kiek bibliotekoje turite knygų */}
                        <Card title="1.2 Kiek bibliotekoje turite knygų? (įrašykite skaičių)" style={{ marginBottom: 16 }}>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name="books_titles_count" label="1.2.1 Knygų pavadinimų">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name="books_copies_count" label="1.2.2 Knygų egzempliorių">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        {/* 1.3 Kiek bibliotekoje vyko renginių */}
                        <Card title="1.3 Kiek bibliotekoje vyko renginių?" style={{ marginBottom: 16 }}>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name="events_count" label="Renginių skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        {/* 1.4 Su kokiomis kitų institucijų bibliotekomis bendradarbiaujate */}
                        <Card title="1.4 Su kokiomis kitų institucijų bibliotekomis bendradarbiaujate?" style={{ marginBottom: 16 }}>
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Form.Item name={['collaborations', 'public']} label="1.4.1 Viešosiomis">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name={['collaborations', 'school']} label="1.4.2 Mokyklų">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name={['collaborations', 'university']} label="1.4.3 Universitetų">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        {/* 1.5 Kaip informuojate skaitytojus */}
                        <Card title="1.5 Kaip informuojate skaitytojus apie naujausius leidinius ir kitus informacinius šaltinius?" style={{ marginBottom: 16 }}>
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Form.Item name={['notification', 'website_lists']} label="1.5.1 Institucijos interneto svetainėje skelbiate naujausių leidinių sąrašus" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name={['notification', 'email_post']} label="1.5.2 Mokyklas informuojate paštu ar elektroniniu paštu" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name={['notification', 'notice_board']} label="1.5.3 Informacija skelbiama institucijos skelbimų lentoje" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                </Col>
                                <Col span={24}>
                                    <Form.Item name={['notification', 'other']} label="1.5.4 Kita (įrašykite)">
                                        <Input />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        {/* 1.6 Ar bibliotekoje kaupiama institucijoje vykusių renginių medžiaga */}
                        <Card title="1.6 Ar bibliotekoje kaupiama institucijoje vykusių renginių medžiaga?" style={{ marginBottom: 16 }}>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name="stores_event_materials" label="Kaupiama renginių medžiaga" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        {/* 1.7 Bibliotekos personalas */}
                        <Card title="1.7 Bibliotekos personalas (įrašykite skaičių)" style={{ marginBottom: 16 }}>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name="staff_full_time" label="1.7.1 Nuolatiniai (etatiniai) darbuotojai">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name="staff_partial" label="1.7.2 Institucijos darbuotojai, turintys ir kitų pareigų">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        {/* 1.8 Ar bibliotekoje kaupiate elektronines informacijos laikmenas */}
                        <Card title="1.8 Ar bibliotekoje kaupiate elektronines informacijos laikmenas?" style={{ marginBottom: 16 }}>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name="has_e_media" label="Kaupiamos elektroninės informacijos laikmenos" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        {/* 1.9 Ar yra elektroninių informacijos laikmenų katalogai/sąrašai */}
                        <Card title="1.9 Ar yra elektroninių informacijos laikmenų katalogai/sąrašai?" style={{ marginBottom: 16 }}>
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Form.Item name={['e_media_catalogs', 'system']} label="1.9.1 Sisteminis" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name={['e_media_catalogs', 'alphabetical']} label="1.9.2 Abėcėlinis" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name={['e_media_catalogs', 'cd_list']} label="1.9.3 Kompaktinių diskų sąrašas" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        {/* 1.10 Kiek turite kompaktinių diskų */}
                        <Card title="1.10 Kiek turite kompaktinių diskų? (įrašykite skaičių)" style={{ marginBottom: 16 }}>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name="cd_titles_count" label="1.10.1 Kompaktinių diskų pavadinimų">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name="cd_copies_count" label="1.10.2 Kompaktinių diskų egzempliorių">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        {/* 1.11 Ar bibliotekos informaciniai šaltiniai skolinami išsinešti */}
                        <Card title="1.11 Ar bibliotekos informaciniai šaltiniai skolinami išsinešti?" style={{ marginBottom: 16 }}>
                            <Row gutter={[24, 24]}>
                                <Col span={12}>
                                    <Title level={5}>Išdavimo leidimai</Title>
                                    {lendingPermissionKeys.map(key => (
                                        <Form.Item
                                            key={key}
                                            name={['lending_permissions', key]}
                                            label={getPermissionLabel(key)}>
                                            <Select
                                                placeholder="Pasirinkite medžiagos tipą"
                                                allowClear>
                                                {materialTypeOptions.map(option => (
                                                    <Option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    ))}
                                </Col>
                            </Row>
                        </Card>

                        {/* 1.12 Ar yra galimybė institucijoje kopijuoti skaitytojams reikalingą bibliotekos medžiagą */}
                        <Card title="1.12 Ar yra galimybė institucijoje kopijuoti skaitytojams reikalingą bibliotekos medžiagą?" style={{ marginBottom: 16 }}>
                            <Row gutter={[24, 24]}>
                                <Col span={12}>
                                    <Title level={5}>Kopijavimo leidimai</Title>
                                    {copyingPermissionKeys.map(key => (
                                        <Form.Item
                                            key={key}
                                            name={['copying_permissions', key]}
                                            label={getPermissionLabel(key)}
                                            valuePropName="checked">
                                            <Switch />
                                        </Form.Item>
                                    ))}
                                </Col>
                            </Row>
                        </Card>

                        {/* 1.13 Ar yra įrengta skaitykla */}
                        <Card title="1.13 Ar yra įrengta skaitykla? (įrašykite vietų skaičių)" style={{ marginBottom: 16 }}>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name={['reading_room_seats', 'print']} label="1.13.1 Spaudinių">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name={['reading_room_seats', 'internet']} label="1.13.2 Interneto">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        {/* 1.14 Bibliotekos darbo laikas */}
                        <Card title="1.14 Bibliotekos darbo laikas (įrašykite)" style={{ marginBottom: 16 }}>
                            <Row gutter={[24, 24]}>
                                <Col span={12} sm={24} md={24} lg={12} xl={12}>
                                    <Title level={5}>Biblioteka</Title>
                                    {['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].map(day => (
                                        <div key={day} style={{ marginBottom: 16 }}>
                                            <Text strong>{getDayLabel(day)}</Text>
                                            <Row gutter={8}>
                                                <Col span={12} style={{ display: 'flex', flexDirection: 'row', gap: 8 }}>
                                                    <Form.Item
                                                        name={['working_hours', 'library', day, 'from']}
                                                        label="Nuo"
                                                        style={{ marginBottom: 8 }}>
                                                        <Select
                                                            className="time-selection"
                                                            placeholder="Pradžia"
                                                            allowClear>
                                                            {timeOptions.map(option => (
                                                                <Option key={option.value} value={option.value}>
                                                                    {option.label}
                                                                </Option>
                                                            ))}
                                                        </Select>
                                                    </Form.Item>
                                                    <Form.Item
                                                        name={['working_hours', 'library', day, 'to']}
                                                        label="Iki"
                                                        style={{ marginBottom: 8 }}>
                                                        <Select
                                                            className="time-selection"
                                                            placeholder="Pabaiga"
                                                            allowClear>
                                                            {timeOptions.map(option => (
                                                                <Option key={option.value} value={option.value}>
                                                                    {option.label}
                                                                </Option>
                                                            ))}
                                                        </Select>
                                                    </Form.Item>
                                                </Col>
                                            </Row>
                                        </div>
                                    ))}
                                </Col>
                                <Col span={12} sm={24} md={24} lg={12} xl={12}>
                                    <Title level={5}>Skaitykla</Title>
                                    {['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].map(day => (
                                        <div key={day} style={{ marginBottom: 16 }}>
                                            <Text strong>{getDayLabel(day)}</Text>
                                            <Row gutter={8}>
                                                <Col span={12} style={{ display: 'flex', flexDirection: 'row', gap: 8 }}>
                                                    <Form.Item
                                                        name={['working_hours', 'reading_room', day, 'from']}
                                                        label="Nuo"
                                                        style={{ marginBottom: 8 }}>
                                                        <Select
                                                            className="time-selection"
                                                            placeholder="Pradžia"
                                                            allowClear>
                                                            {timeOptions.map(option => (
                                                                <Option key={option.value} value={option.value}>
                                                                    {option.label}
                                                                </Option>
                                                            ))}
                                                        </Select>
                                                    </Form.Item>
                                                    <Form.Item
                                                        name={['working_hours', 'reading_room', day, 'to']}
                                                        label="Iki"
                                                        style={{ marginBottom: 8 }}>
                                                        <Select
                                                            className="time-selection"
                                                            placeholder="Pabaiga"
                                                            allowClear>
                                                            {timeOptions.map(option => (
                                                                <Option key={option.value} value={option.value}>
                                                                    {option.label}
                                                                </Option>
                                                            ))}
                                                        </Select>
                                                    </Form.Item>
                                                </Col>
                                            </Row>
                                        </div>
                                    ))}
                                </Col>
                            </Row>
                        </Card>

                        {/* 1.15 Kiek skaitytojų aptarnauta per ataskaitinį laikotarpį */}
                        <Card title="1.15 Kiek skaitytojų aptarnauta per ataskaitinį laikotarpį? (įrašykite skaičių)" style={{ marginBottom: 16 }}>
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Form.Item name={['readers_stats', 'institution_staff']} label="1.15.2 Institucijos darbuotojų">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name={['readers_stats', 'lecturers']} label="1.15.3 Lektorių">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name={['readers_stats', 'event_participants']} label="1.15.4 Institucijos renginių dalyvių">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name={['readers_stats', 'region_teachers']} label="1.15.5 Regiono švietimo darbuotojų">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name={['readers_stats', 'students']} label="1.15.6 Mokinių">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name={['readers_stats', 'other', 'count']} label="1.15.7 Kitų">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={24}>
                                    <Form.Item name={['readers_stats', 'other', 'label']} label="Kitų kategorijos pavadinimas">
                                        <Input />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        {/* 1.16 Ar tiriami bibliotekos skaitytojų poreikiai */}
                        <Card title="1.16 Ar tiriami bibliotekos skaitytojų poreikiai?" style={{ marginBottom: 16 }}>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name={['surveys', 'conducted']} label="Tiriami skaitytojų poreikiai" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        {/* 1.17 Kaip tiriami skaitytojų poreikiai */}
                        <Card title="1.17 Kaip tiriami skaitytojų poreikiai?" style={{ marginBottom: 16 }}>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name={['surveys', 'methods', 'requests_analysis']} label="1.17.1 Registruojamos ir analizuojamos įvykdytos ir neįvykdytos skaitytojų užklausos" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name={['surveys', 'methods', 'institution_surveys']} label="1.17.2 Pagal institucijos vykdomų apklausų duomenis" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                </Col>
                                <Col span={24}>
                                    <Form.Item name={['surveys', 'methods', 'other']} label="1.17.3 Kita (įrašykite)">
                                        <Input />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        {/* 1.18 Kokia dalimi biblioteka tenkina skaitytojų poreikius */}
                        <Card title="1.18 Kokia dalimi biblioteka, Jūsų nuomone, tenkina skaitytojų poreikius? (nurodykite procentais)" style={{ marginBottom: 16 }}>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name="satisfaction_percent" label="Skaitytojų pasitenkinimas (%)">
                                        <InputNumber min={0} max={100} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        {/* Form Actions */}
                        <div style={{ textAlign: 'right', marginTop: 24 }}>
                            <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
                                Išsaugoti
                            </Button>
                        </div>
                    </Form>
                </Card>
            </div>
        </ConfigProvider>
    );
};

export default ScLibrary;
